import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import { RENTER_INVITE_QUERY_PARAM } from '../constants';

// Keeps ?renter-invite=true in sync with drawer state so analytics tools can detect it via URL.
export const useInviteDrawerQueryParamSync = () => {
  const { isOpen } = useRenterInviteDrawerStore();
  const [, setSearchParams] = useSearchParams();

  // Stable ref so the unmount cleanup doesn't re-register when setSearchParams identity changes.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const prevOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setSearchParamsRef.current(
        (prev) => {
          if (prev.get(RENTER_INVITE_QUERY_PARAM) === 'true') return prev;
          prev.set(RENTER_INVITE_QUERY_PARAM, 'true');
          return prev;
        },
        { replace: true },
      );
    }

    if (!isOpen && prevOpenRef.current) {
      setSearchParamsRef.current(
        (prev) => {
          prev.delete(RENTER_INVITE_QUERY_PARAM);
          return prev;
        },
        { replace: true },
      );
    }

    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // Remove param on unmount in case the drawer was open during a route change.
  useEffect(() => {
    return () => {
      if (prevOpenRef.current) {
        setSearchParamsRef.current(
          (prev) => {
            prev.delete(RENTER_INVITE_QUERY_PARAM);
            return prev;
          },
          { replace: true },
        );
      }
    };
  }, []);
};
